import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { addDays, format } from 'date-fns';
import {
  CalendarDays,
  CalendarRange,
  Check,
  ChevronDown,
  Clock,
  Loader2,
  Search,
  Sun,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '@/components/atoms/button';
import { DatePicker } from '@/components/molecules/date-picker';
import { Input } from '@/components/atoms/input';
import { NumberInput } from '@/components/molecules/NumberInput';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  AppointmentSlotPicker,
  type AppointmentWizardSlot,
} from '@/components/organisms/jobs/AppointmentSlotWizard';
import { dateKeyInTimeZone, dateStringToDate, dateToDateString } from '@/lib/date-values';
import { businessDayMinutes } from '@/lib/scheduler/businessDayMinutes';
import {
  missingSkillSuffix,
  sortQualifiedFirst,
  type RequiredSkillRef,
} from '@/lib/scheduler/technicianQualification';
import {
  BUSINESS_DAY_PRESET_LABEL,
  DEFAULT_DURATION_PRESETS,
  type DurationPreset,
} from '@/lib/scheduler/durationPresets';
import { useBusinessSettings } from '@/hooks/settings/useBusinessSettings';
import type { TimeEntryBinding } from '@/hooks/useTimeEntry';
import { cn } from '@/lib/utils';
import type { PreferredTimeWindow, Urgency } from '@/types/jobAppointment';
import type { AvailabilityDayCandidate, SchedulerTechnician } from '@/types/scheduler';

/**
 * Scheduling v2 — time-shape choices for a visit's schedule step. Product
 * "All day" maps to backend DAY_FLEXIBLE; "Full day" and "Multi-day" map
 * to backend ALL_DAY (single day or consecutiveDays series).
 *
 * Sentence case, and "Multi-day" rather than "Full Days" — Job Type settings
 * already called it that, and the two screens disagreeing about the name of a
 * value one of them sets as the default for the other is its own bug.
 */
export type VisitShape = 'TIMED' | 'ALL_DAY' | 'FULL_DAY' | 'MULTI_DAY';

export const VISIT_SHAPE_OPTIONS: Array<{
  value: VisitShape;
  icon: LucideIcon;
  title: string;
  label: string;
}> = [
  { value: 'TIMED', icon: Clock, title: 'Timed visit', label: 'Timed' },
  { value: 'ALL_DAY', icon: CalendarDays, title: 'All day — no set time', label: 'All day' },
  { value: 'FULL_DAY', icon: Sun, title: 'Full day — blocks the shift', label: 'Full day' },
  { value: 'MULTI_DAY', icon: CalendarRange, title: 'Multiple full days', label: 'Multi-day' },
];

export function backendGranularityForVisitShape(
  visitShape: VisitShape,
): 'TIMED' | 'DAY_FLEXIBLE' | 'ALL_DAY' {
  if (visitShape === 'ALL_DAY') return 'DAY_FLEXIBLE';
  if (visitShape === 'FULL_DAY' || visitShape === 'MULTI_DAY') return 'ALL_DAY';
  return 'TIMED';
}

export function visitShapeDisplayLabel(visitShape: VisitShape): string {
  return VISIT_SHAPE_OPTIONS.find((option) => option.value === visitShape)?.label ?? 'Timed';
}

export function deriveMultiDayDateTo(dateFrom: string, consecutiveDaysInput: string): string {
  const start = dateStringToDate(dateFrom);
  if (!start) return dateFrom;
  return dateToDateString(addDays(start, clampConsecutiveDays(consecutiveDaysInput) - 1));
}

/** 'YYYY-MM-DD' → "Fri Jul 10" (a tz-naive business day, rendered as-is). */
function formatDayLabel(value: string): string {
  const date = dateStringToDate(value);
  return date ? format(date, 'EEE MMM d') : value;
}

function todayInTimeZone(timeZone?: string | null): Date | undefined {
  if (!timeZone) return undefined;
  return dateStringToDate(dateKeyInTimeZone(new Date().toISOString(), timeZone));
}

export function dayCandidateKey(candidate: AvailabilityDayCandidate): string {
  return `${candidate.date}:${candidate.technicianId}`;
}

function dayCandidateGroupKey(candidate: AvailabilityDayCandidate): string {
  return candidate.dates?.length ? candidate.dates.join(':') : candidate.date;
}

/** "Fri Jul 10" for a single day, "Mon Jul 13 → Wed Jul 15" for a run. */
export function dayCandidateLabel(candidate: AvailabilityDayCandidate): string {
  const run = candidate.dates && candidate.dates.length > 1 ? candidate.dates : null;
  return run
    ? `${formatDayLabel(run[0])} → ${formatDayLabel(run[run.length - 1])}`
    : formatDayLabel(candidate.date);
}

/** Keep the multi-day series length within the API's accepted 2..14 days. */
export function clampConsecutiveDays(value: string): number {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return 2;
  return Math.min(14, Math.max(2, parsed));
}

/** Date-first picker: choose a day, then the best technician for that day. */
export function DayCandidatePicker({
  candidates,
  selectedKey,
  onSelect,
  emptyReason,
}: {
  candidates: AvailabilityDayCandidate[];
  selectedKey: string | null;
  onSelect: (candidate: AvailabilityDayCandidate) => void;
  emptyReason?: string | null;
}) {
  const groups = useMemo(() => {
    const grouped = new Map<string, AvailabilityDayCandidate[]>();
    for (const candidate of candidates) {
      const groupKey = dayCandidateGroupKey(candidate);
      const existing = grouped.get(groupKey);
      if (existing) existing.push(candidate);
      else grouped.set(groupKey, [candidate]);
    }
    return [...grouped.entries()].sort(([firstKey], [secondKey]) =>
      firstKey.localeCompare(secondKey),
    );
  }, [candidates]);
  const selectedCandidate = candidates.find(
    (candidate) => dayCandidateKey(candidate) === selectedKey,
  );
  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);

  useEffect(() => {
    const selectedGroupKey = selectedCandidate ? dayCandidateGroupKey(selectedCandidate) : null;
    const groupStillExists = groups.some(([groupKey]) => groupKey === activeGroupKey);
    if (selectedGroupKey) setActiveGroupKey(selectedGroupKey);
    else if (!groupStillExists) setActiveGroupKey(groups[0]?.[0] ?? null);
  }, [activeGroupKey, groups, selectedCandidate]);

  if (candidates.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl border py-8 text-center text-sm text-muted-foreground">
        <p>No available days found.</p>
        {emptyReason ? <p className="max-w-sm text-xs">{emptyReason}</p> : null}
      </div>
    );
  }

  const visibleGroupKey = activeGroupKey ?? groups[0]?.[0] ?? null;
  const visibleCandidates = groups.find(([groupKey]) => groupKey === visibleGroupKey)?.[1] ?? [];

  return (
    <div className="space-y-3">
      <div className="flex gap-2 overflow-x-auto pb-1" aria-label="Available days">
        {groups.map(([groupKey, dayCandidates]) => {
          const firstCandidate = dayCandidates[0];
          const isActive = visibleGroupKey === groupKey;
          return (
            <button
              key={groupKey}
              type="button"
              onClick={() => {
                setActiveGroupKey(groupKey);
                onSelect(dayCandidates[0]);
              }}
              className={cn(
                'min-w-[7.25rem] shrink-0 rounded-md border px-3 py-2 text-left transition-colors hover:bg-muted/40',
                isActive && 'border-foreground bg-muted/50',
              )}
              aria-pressed={isActive}
            >
              <span className="block text-xs font-medium uppercase text-muted-foreground">
                {firstCandidate.dates?.length
                  ? `${firstCandidate.dates.length} days`
                  : format(dateStringToDate(firstCandidate.date)!, 'EEE')}
              </span>
              <span className="block text-sm font-semibold">
                {firstCandidate.dates?.length
                  ? dayCandidateLabel(firstCandidate)
                  : format(dateStringToDate(firstCandidate.date)!, 'MMM d')}
              </span>
              <span className="block text-xs text-muted-foreground">
                {dayCandidates.length} {dayCandidates.length === 1 ? 'technician' : 'technicians'}
              </span>
            </button>
          );
        })}
      </div>

      <div
        className="divide-y divide-border/50 overflow-hidden rounded-md border"
        aria-label="Available technicians"
      >
        {visibleCandidates.map((candidate) => {
          const key = dayCandidateKey(candidate);
          const isSelected = selectedKey === key;
          const freeHours = Math.round((candidate.remainingMinutes / 60) * 10) / 10;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelect(candidate)}
              className={cn(
                'flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-muted/40',
                isSelected && 'bg-primary/5',
              )}
            >
              <div
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
                  isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/30',
                )}
              >
                {isSelected && <Check size={9} className="text-primary-foreground" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{candidate.technicianName}</p>
                <p className="truncate text-xs text-muted-foreground">{freeHours}h free</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface AvailabilityDayFieldsProps {
  visitShape: VisitShape;
  technicians?: SchedulerTechnician[];
  /** Skills the visit needs — qualified techs sort first, others get a "· Missing: …" suffix. */
  requiredSkills?: RequiredSkillRef[];
  technicianId?: string;
  /** A selected crew determines its lead; edit membership in the crew picker. */
  technicianSelectionDisabled?: boolean;
  dateFrom: string;
  dateTo: string;
  urgency: Urgency;
  consecutiveDaysInput: string;
  daysSearched: boolean;
  searching: boolean;
  searchDisabled?: boolean;
  candidates: AvailabilityDayCandidate[];
  selectedDayKey: string | null;
  emptyReason?: string | null;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onTechnicianChange?: (value: string) => void;
  onUrgencyChange: (value: Urgency) => void;
  onConsecutiveDaysChange: (value: string) => void;
  showControls?: boolean;
  /** Renders the idle-state "Search days" button when provided. */
  onSearchDays?: () => void;
  onSelectDay: (candidate: AvailabilityDayCandidate) => void;
}

/**
 * Scheduling v2 day-search fields. All Day uses route insertion candidates;
 * Full Day / Full Days use POST /availability/days. The controls and result
 * cards stay shared so the three day-bound shapes remain visually consistent.
 */
export function AvailabilityDayFields({
  visitShape,
  technicians = [],
  requiredSkills,
  technicianId = 'all',
  technicianSelectionDisabled = false,
  dateFrom,
  dateTo,
  urgency,
  consecutiveDaysInput,
  daysSearched,
  searching,
  searchDisabled = false,
  candidates,
  selectedDayKey,
  emptyReason,
  onDateFromChange,
  onDateToChange,
  onTechnicianChange,
  onUrgencyChange,
  onConsecutiveDaysChange,
  showControls = true,
  onSearchDays,
  onSelectDay,
}: AvailabilityDayFieldsProps) {
  const { data: businessSettings } = useBusinessSettings();
  const businessToday = todayInTimeZone(businessSettings?.timezone);
  const isMultiDay = visitShape === 'MULTI_DAY';
  const rankedTechnicians = useMemo(
    () => sortQualifiedFirst(technicians, requiredSkills),
    [technicians, requiredSkills],
  );
  const daySeriesControl = (
    <div className="space-y-1.5">
      <Label>Days</Label>
      <Input
        type="number"
        min={2}
        max={14}
        className="w-full"
        value={consecutiveDaysInput}
        onChange={(event) => onConsecutiveDaysChange(event.target.value)}
      />
    </div>
  );

  return (
    <div className="space-y-3">
      {showControls && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="min-w-0 space-y-1.5">
              <Label>Technicians</Label>
              <Select
                value={technicianId}
                onValueChange={onTechnicianChange}
                disabled={technicianSelectionDisabled}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Best available</SelectItem>
                  {rankedTechnicians.map(({ technician, missing }) => (
                    <SelectItem key={technician.id} value={technician.id}>
                      {technician.name}
                      {missing.length > 0 ? (
                        <span className="text-muted-foreground">
                          {' '}
                          · {missingSkillSuffix(missing)}
                        </span>
                      ) : null}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Booking urgency</Label>
              <Select value={urgency} onValueChange={(value) => onUrgencyChange(value as Urgency)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div
            className={cn(
              'grid grid-cols-1 gap-3',
              isMultiDay ? 'sm:grid-cols-[minmax(0,1fr)_9rem]' : 'sm:grid-cols-2',
            )}
          >
            <div className="space-y-1.5">
              <Label>{isMultiDay ? 'Start date' : 'From Date'}</Label>
              <DatePicker
                value={dateStringToDate(dateFrom)}
                today={businessToday}
                onChange={(date) => onDateFromChange(dateToDateString(date))}
                placeholder="Pick a date"
              />
            </div>
            {isMultiDay ? (
              daySeriesControl
            ) : (
              <div className="space-y-1.5">
                <Label>To Date</Label>
                <DatePicker
                  value={dateStringToDate(dateTo)}
                  today={businessToday}
                  onChange={(date) => onDateToChange(dateToDateString(date))}
                  placeholder="Pick a date"
                />
              </div>
            )}
          </div>
          {isMultiDay ? (
            <p className="text-xs text-muted-foreground">
              Runs forward from the start date with the same technician or crew.
            </p>
          ) : null}
        </>
      )}
      {!daysSearched ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed px-3 py-2 text-sm text-muted-foreground">
          <p>
            {!showControls
              ? 'Search available days.'
              : visitShape === 'MULTI_DAY'
                ? 'Search consecutive working days.'
                : visitShape === 'FULL_DAY'
                  ? 'Search fully free working days.'
                  : 'Search days with enough capacity.'}
          </p>
          {onSearchDays ? (
            <Button
              type="button"
              size="sm"
              disabled={searchDisabled || searching}
              onClick={onSearchDays}
            >
              {searching ? (
                <Loader2 size={14} className="mr-1.5 animate-spin" />
              ) : (
                <Search size={14} className="mr-1.5" />
              )}
              Search days
            </Button>
          ) : null}
        </div>
      ) : (
        <DayCandidatePicker
          candidates={candidates}
          selectedKey={selectedDayKey}
          onSelect={onSelectDay}
          emptyReason={emptyReason}
        />
      )}
    </div>
  );
}

function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours && mins) return `${hours}h ${mins}m`;
  if (hours) return `${hours}h`;
  return `${mins}m`;
}

/** Keep a custom duration within the availability API's accepted 1..1440 minutes. */
function clampDuration(minutes: number): number {
  return Math.min(1440, Math.max(1, minutes));
}

type DurationDraft = { hours: string; minutes: string };

const DURATION_NOT_WHOLE = 'Use whole hours and minutes.';
const DURATION_TOO_SHORT = 'Duration must be at least 1 minute.';
const DURATION_TOO_LONG = 'Duration can be at most 24 hours.';

function splitDuration(minutes: number): DurationDraft {
  return { hours: String(Math.floor(minutes / 60)), minutes: String(minutes % 60) };
}

/** Both halves are whole numbers, or empty. */
function draftIsWhole(draft: DurationDraft): boolean {
  return /^\d*$/.test(draft.hours) && /^\d*$/.test(draft.minutes);
}

/** Total minutes of a draft; an empty half counts as 0 of that unit. */
function draftTotal(draft: DurationDraft): number {
  return Number(draft.hours || 0) * 60 + Number(draft.minutes || 0);
}

/** Why the availability API (1..1440 minutes) would refuse this total, if it would. */
function durationProblem(total: number): string | null {
  if (total < 1) return DURATION_TOO_SHORT;
  if (total > 1440) return DURATION_TOO_LONG;
  return null;
}

/** Why the form cannot take what the boxes hold, if it cannot. */
function draftProblem(draft: DurationDraft): string | null {
  return draftIsWhole(draft) ? durationProblem(draftTotal(draft)) : DURATION_NOT_WHOLE;
}

/**
 * Compact "h | m" duration input shared by the inline schedule controls.
 *
 * Both halves are kept as typed while the field has focus, and the total is
 * built from the two drafts. Rebuilding it from the hours re-split out of the
 * last total counted a rollover twice ("600" minutes at 1h came out as 12h),
 * and left a "90" sitting in the minutes box beside hours that had already
 * taken the hour. Leaving the field writes the total back as hours and
 * minutes, so 90m reads 1h 30m.
 *
 * Only whole numbers are taken, and a total the API refuses (zero, or more
 * than 24 hours) is never reported: the field says why as it is typed. In a
 * form that saves (`entry`), such a duration stays on screen and blocks Save
 * until it is corrected. Elsewhere, leaving the field puts back the duration
 * it had before the typing began.
 */
export function CompactDurationInput({
  durationMinutes,
  onDurationChange,
  idPrefix,
  entry,
}: {
  durationMinutes: number;
  onDurationChange: (minutes: number) => void;
  idPrefix: string;
  /**
   * The form's `useTimeEntry().binding.duration`. Through it the field says
   * when it shows a duration the form cannot take, and the form's Save checks
   * the boxes first: a tap on Save does not blur the field on every touch
   * browser, and must not save the earlier duration under a red message.
   */
  entry?: TimeEntryBinding['duration'];
}) {
  const [draft, setDraft] = useState<DurationDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The duration when this round of typing began. Leaving the field with a box
  // that is not a whole number, or with an out-of-range total, puts it back.
  const typingStart = useRef<number | null>(null);
  const shown = draft ?? splitDuration(durationMinutes);
  const errorId = `${idPrefix}-duration-error`;

  // The form holds the typed total only while it is one leaving would keep;
  // otherwise it holds the duration from before the typing, the one leaving
  // puts back. A Save tapped without blurring first must not take the 3h that
  // "30" hours passed through.
  function publish(total: number | null) {
    const kept = total ?? typingStart.current ?? durationMinutes;
    if (kept !== durationMinutes) onDurationChange(kept);
  }

  // A box keeps exactly what was typed, "1.5" included, and is judged on that
  // alone. Refusing the "." left "15" on screen, and no rule for whether the
  // next digits continued "1.5" or replaced it held for every edit.
  function edit(part: keyof DurationDraft, raw: string) {
    typingStart.current ??= durationMinutes;
    const next = { ...shown, [part]: raw.slice(0, 4) };
    setDraft(next);
    // Said as it is typed, never only on leaving: whenever the form is holding
    // something other than what the boxes show, the field says why.
    const problem = draftProblem(next);
    setError(problem);
    entry?.report(Boolean(problem));
    publish(problem ? null : draftTotal(next));
  }

  function settle(event: React.FocusEvent<HTMLDivElement>) {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    const start = typingStart.current;
    typingStart.current = null;
    const problem = draft ? draftProblem(draft) : null;
    // A form that saves keeps what was typed on screen, with its message, and
    // will not save until it is corrected.
    if (problem && entry) return;
    if (problem && start !== null) publish(start);
    setError(problem);
    setDraft(null);
  }

  // Save checks what the boxes hold, whether or not its press left the field.
  const binding = useRef(entry);
  useLayoutEffect(() => {
    binding.current = entry;
    if (entry) {
      entry.commitRef.current = () => {
        const problem = draft ? draftProblem(draft) : null;
        setError(problem);
        return !problem;
      };
    }
  });
  // A field that leaves the screen holds nothing to correct.
  useEffect(
    () => () => {
      binding.current?.report(false);
      if (binding.current) binding.current.commitRef.current = null;
    },
    [],
  );

  const inputProps = {
    type: 'text',
    inputMode: 'numeric',
    autoComplete: 'off',
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? errorId : undefined,
    className: 'h-full w-0 min-w-0 flex-1 bg-transparent px-2 text-sm outline-none',
  } as const;

  return (
    <div className="min-w-0">
      <div
        data-slot="duration-input"
        onBlur={settle}
        className={cn(
          'flex h-9 min-w-0 items-center rounded-md border border-input bg-transparent shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 dark:bg-input/30',
          error &&
            'border-destructive focus-within:border-destructive focus-within:ring-destructive/20',
        )}
      >
        <input
          id={`${idPrefix}-hours`}
          aria-label="Hours"
          value={shown.hours}
          onChange={(event) => edit('hours', event.target.value)}
          {...inputProps}
        />
        <span className="shrink-0 pr-2 text-xs text-muted-foreground">h</span>
        <span aria-hidden className="h-5 w-px shrink-0 bg-border" />
        <input
          id={`${idPrefix}-minutes`}
          aria-label="Minutes"
          value={shown.minutes}
          onChange={(event) => edit('minutes', event.target.value)}
          {...inputProps}
        />
        <span className="shrink-0 pr-2 text-xs text-muted-foreground">m</span>
      </div>
      {error ? (
        <p id={errorId} role="alert" className="mt-1 text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function endTimeForDuration(startTime: string, durationMinutes: number): string {
  const match = /^(\d{2}):(\d{2})$/.exec(startTime);
  if (!match) return '';
  const startMinutes = Number(match[1]) * 60 + Number(match[2]);
  if (!Number.isFinite(startMinutes)) return '';
  const endMinutes = (startMinutes + clampDuration(durationMinutes)) % (24 * 60);
  return `${String(Math.floor(endMinutes / 60)).padStart(2, '0')}:${String(
    endMinutes % 60,
  ).padStart(2, '0')}`;
}

interface AppointmentScheduleFieldsProps {
  technicians: SchedulerTechnician[];
  /** Skills the visit needs — qualified techs sort first, others get a "· Missing: …" suffix. */
  requiredSkills?: RequiredSkillRef[];
  dateFrom: string;
  dateTo: string;
  technicianId: string;
  preferredTimeWindow: PreferredTimeWindow;
  urgency: Urgency;
  durationMinutes: number | null;
  durationPresets?: DurationPreset[];
  /**
   * Length (minutes) the "Business hours" preset resolves to. When omitted the
   * component derives it from the tenant's business hours for the weekday of
   * `dateFrom` (falling back to 480 when no hours are configured).
   */
  fullDayMinutes?: number;
  jobTypeDefaultDuration?: number | null;
  slotsSearched: boolean;
  slots: AppointmentWizardSlot[];
  selectedSlot?: AppointmentWizardSlot | null;
  selectedSlotId?: string | null;
  emptyReason?: string | null;
  idleSlotContent?: ReactNode;
  showDateRange?: boolean;
  searching?: boolean;
  searchDisabled?: boolean;
  /** Copy for the empty state's search button. Defaults to "Search availability". */
  searchLabel?: string;
  showControls?: boolean;
  /** Create-job quick search: compact h/m duration + a single 3-col controls row. */
  compactLayout?: boolean;
  /** One-off timed controls that share the compact two-row grid. */
  compactLeadingControls?: ReactNode;
  /**
   * Controls the caller wants demoted behind a "More options" disclosure.
   * Passing this also demotes time-of-day and booking urgency, leaving
   * technician, duration and the date range — what the search runs on — visible.
   * compactLayout only.
   */
  moreOptions?: ReactNode;
  /** One-off create only: null means a broad window; a value means an exact preferred start. */
  specificStartTime?: string | null;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onTechnicianChange: (value: string) => void;
  onPreferredTimeWindowChange: (value: PreferredTimeWindow) => void;
  onUrgencyChange: (value: Urgency) => void;
  onDurationChange: (minutes: number) => void;
  onSpecificStartTimeChange?: (value: string | null) => void;
  onSearchAvailability?: () => void;
  onSelectSlot: (slot: AppointmentWizardSlot) => void;
  /** Open the crew editor from the results — two people, a lead and a helper. */
  onChooseCrew?: () => void;
  /** Offered from the results empty state. */
  onEnterManually?: () => void;
  /** Who is going, out of the people free at the chosen time — lead first. */
  selectedTechnicianIds?: string[];
  onSelectedTechniciansChange?: (slot: AppointmentWizardSlot, technicianIds: string[]) => void;
}

export function AppointmentScheduleFields({
  technicians,
  requiredSkills,
  dateFrom,
  dateTo,
  technicianId,
  preferredTimeWindow,
  urgency,
  durationMinutes,
  durationPresets = DEFAULT_DURATION_PRESETS,
  fullDayMinutes,
  jobTypeDefaultDuration,
  slotsSearched,
  slots,
  selectedSlot,
  selectedSlotId,
  emptyReason,
  idleSlotContent,
  showDateRange = true,
  searching = false,
  searchDisabled = false,
  searchLabel = 'Search availability',
  showControls = true,
  compactLayout = false,
  compactLeadingControls,
  moreOptions,
  specificStartTime,
  onDateFromChange,
  onDateToChange,
  onTechnicianChange,
  onPreferredTimeWindowChange,
  onUrgencyChange,
  onDurationChange,
  onSpecificStartTimeChange,
  onSearchAvailability,
  onSelectSlot,
  onChooseCrew,
  onEnterManually,
  selectedTechnicianIds,
  onSelectedTechniciansChange,
}: AppointmentScheduleFieldsProps) {
  const [showCustom, setShowCustom] = useState(false);

  // "Business hours" = the tenant's real business-day length for the selected
  // weekday (falls back to 480 when no hours are configured). An explicit
  // `fullDayMinutes` prop overrides this per call-site.
  const { data: businessSettings } = useBusinessSettings();
  const businessToday = todayInTimeZone(businessSettings?.timezone);
  const weekday = (dateStringToDate(dateFrom) ?? new Date()).getDay();
  const computedFullDay = businessDayMinutes(
    businessSettings?.businessHours,
    weekday,
    businessSettings?.businessHoursStart,
    businessSettings?.businessHoursEnd,
  );
  const resolvedFullDay = fullDayMinutes ?? computedFullDay;
  const expandedPresets = durationPresets.map((preset) =>
    preset.label === BUSINESS_DAY_PRESET_LABEL ? { ...preset, minutes: resolvedFullDay } : preset,
  );
  const resolvedPresets = expandedPresets.filter((preset, index) => {
    if (preset.label !== BUSINESS_DAY_PRESET_LABEL) return true;
    return !expandedPresets.some(
      (candidate, candidateIndex) => candidateIndex < index && candidate.minutes === preset.minutes,
    );
  });

  const activePresetIndex =
    durationMinutes == null
      ? -1
      : resolvedPresets.findIndex((preset) => preset.minutes === durationMinutes);
  const matchesPreset = activePresetIndex !== -1;
  const hasCustomValue = durationMinutes != null && !matchesPreset;
  const customOpen = showCustom || hasCustomValue;
  const customChipLabel =
    hasCustomValue && durationMinutes != null ? formatDuration(durationMinutes) : 'Custom…';

  const currentHours = durationMinutes != null ? Math.floor(durationMinutes / 60) : 0;
  const currentMinutes = durationMinutes != null ? durationMinutes % 60 : 0;

  const chipClass = (active: boolean) =>
    cn(
      'inline-flex min-h-9 w-full items-center justify-center rounded-full border px-3 py-1 text-center text-sm font-medium transition-colors',
      active ? 'bg-blue-50 text-blue-700 border-blue-200' : 'border text-foreground hover:bg-muted',
    );

  const durationControl = (
    <div className="space-y-1.5">
      <Label>Duration</Label>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {resolvedPresets.map((preset, index) => (
          <button
            key={`${preset.label}-${preset.minutes}`}
            type="button"
            className={chipClass(index === activePresetIndex)}
            onClick={() => {
              setShowCustom(false);
              onDurationChange(preset.minutes);
            }}
          >
            {preset.label}
          </button>
        ))}
        <button type="button" className={chipClass(customOpen)} onClick={() => setShowCustom(true)}>
          {customChipLabel}
        </button>
      </div>
      {customOpen ? (
        <div className="flex items-center gap-2 pt-1">
          <NumberInput
            min={0}
            className="w-20"
            aria-label="Hours"
            value={durationMinutes != null && currentHours ? currentHours : null}
            emptyValue={0}
            allowDecimal={false}
            onChange={(next) => onDurationChange(clampDuration((next ?? 0) * 60 + currentMinutes))}
          />
          <span className="text-sm text-muted-foreground">h</span>
          <NumberInput
            min={0}
            max={59}
            className="w-20"
            aria-label="Minutes"
            value={durationMinutes != null && currentMinutes ? currentMinutes : null}
            emptyValue={0}
            allowDecimal={false}
            onChange={(next) => onDurationChange(clampDuration(currentHours * 60 + (next ?? 0)))}
          />
          <span className="text-sm text-muted-foreground">m</span>
        </div>
      ) : null}
      {durationMinutes == null ? (
        <p className="text-xs text-muted-foreground">Defaults to your slot length (60 min).</p>
      ) : jobTypeDefaultDuration != null && durationMinutes === jobTypeDefaultDuration ? (
        <p className="text-xs text-muted-foreground">Default for this job type.</p>
      ) : null}
    </div>
  );
  const scheduleControlsGridClass = cn(
    'grid grid-cols-1 gap-3',
    technicians.length > 0 ? 'sm:grid-cols-3' : 'sm:grid-cols-2',
  );
  const rankedTechnicians = useMemo(
    () => sortQualifiedFirst(technicians, requiredSkills),
    [technicians, requiredSkills],
  );
  const technicianControl = (
    <>
      {technicians.length > 0 && (
        <div className={cn('space-y-1.5', compactLayout && 'min-w-0 lg:col-span-3')}>
          <Label>Technician</Label>
          <Select value={technicianId} onValueChange={onTechnicianChange}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Best available</SelectItem>
              {rankedTechnicians.map(({ technician, missing }) => (
                <SelectItem key={technician.id} value={technician.id}>
                  {technician.name}
                  {missing.length > 0 ? (
                    <span className="text-muted-foreground"> · {missingSkillSuffix(missing)}</span>
                  ) : null}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </>
  );

  /*
   * Time of day and booking urgency: real controls, but ones a dispatcher
   * changes on a minority of jobs. Kept as their own fragment so the compact
   * layout can demote them behind "More options" while technician, duration and
   * the date range — the three the search actually runs on — stay in view.
   */
  const preferenceControls = (
    <>
      <div className={cn('space-y-1.5', compactLayout && 'min-w-0 lg:col-span-3')}>
        <Label>{onSpecificStartTimeChange ? 'Time of day' : 'Preferred window'}</Label>
        <Select
          value={
            specificStartTime !== null && specificStartTime !== undefined
              ? 'specific'
              : preferredTimeWindow
          }
          onValueChange={(value) => {
            if (value === 'specific') {
              onSpecificStartTimeChange?.(specificStartTime || '09:00');
              return;
            }
            onSpecificStartTimeChange?.(null);
            onPreferredTimeWindowChange(value as PreferredTimeWindow);
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any time</SelectItem>
            <SelectItem value="morning">Morning</SelectItem>
            <SelectItem value="afternoon">Afternoon</SelectItem>
            <SelectItem value="evening">Evening</SelectItem>
            {onSpecificStartTimeChange ? (
              <SelectItem value="specific">A set time</SelectItem>
            ) : null}
          </SelectContent>
        </Select>
      </div>
      <div className={cn('space-y-1.5', compactLayout && 'min-w-0 lg:col-span-3')}>
        {/* "Booking urgency", not "Time-window urgency": it has nothing to do
            with the arrival window or the time-of-day preference above it. */}
        <Label>Booking urgency</Label>
        <Select value={urgency} onValueChange={(value) => onUrgencyChange(value as Urgency)}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="standard">Standard</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="emergency">Emergency</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </>
  );

  /** Every caller that doesn't demote gets the original single fragment. */
  const scheduleControls = (
    <>
      {technicianControl}
      {preferenceControls}
    </>
  );

  const dateRangeFields = (
    <>
      <div className={cn('space-y-1.5', compactLayout && 'min-w-0 lg:col-span-3')}>
        {/* "Search from / Search until" — these bound the availability search,
            they are not the visit's own dates, which "From/To Date" implied. */}
        <Label>Search from</Label>
        <DatePicker
          value={dateStringToDate(dateFrom)}
          today={businessToday}
          onChange={(date) => onDateFromChange(dateToDateString(date))}
          placeholder="Pick a date"
        />
      </div>
      <div className={cn('space-y-1.5', compactLayout && 'min-w-0 lg:col-span-3')}>
        <Label>Search until</Label>
        <DatePicker
          value={dateStringToDate(dateTo)}
          today={businessToday}
          onChange={(date) => onDateToChange(dateToDateString(date))}
          placeholder="Pick a date"
        />
      </div>
    </>
  );

  // Create-job quick search: compact controls share one wrapping row when the
  // scheduling panel has enough room.
  const compactDurationControl = (
    // Three columns like every other control in this grid. At two it was the
    // one odd width in the block, which made the second row read as ragged
    // rather than as a continuation of the first.
    <div className="min-w-0 space-y-1.5 lg:col-span-3">
      <Label>Duration</Label>
      <CompactDurationInput
        idPrefix="appointment-duration"
        durationMinutes={durationMinutes ?? 60}
        onDurationChange={onDurationChange}
      />
    </div>
  );
  const specificTimeControl =
    onSpecificStartTimeChange && specificStartTime !== null && specificStartTime !== undefined ? (
      <div className="grid min-w-0 grid-cols-2 gap-3 lg:col-span-4">
        <div className="space-y-1.5">
          <Label htmlFor="appointment-specific-start">
            {compactLayout ? 'Start' : 'Preferred start'}
          </Label>
          <Input
            id="appointment-specific-start"
            type="time"
            value={specificStartTime}
            onChange={(event) => onSpecificStartTimeChange(event.target.value)}
            className={cn(compactLayout && 'px-2 text-xs')}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="appointment-specific-end">{compactLayout ? 'End' : 'Ends at'}</Label>
          <Input
            id="appointment-specific-end"
            type="time"
            readOnly
            tabIndex={-1}
            value={endTimeForDuration(specificStartTime, durationMinutes ?? 60)}
            className={cn(
              'border-dashed bg-muted/20 text-muted-foreground',
              compactLayout && 'px-2 text-xs',
            )}
          />
        </div>
      </div>
    ) : null;

  return (
    <>
      {showControls &&
        (compactLayout ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-2 lg:grid-cols-12">
              {compactLeadingControls}
              {showDateRange && dateRangeFields}
              {/* Duration rides with the other refinements when a More-options
                  panel exists: it comes from the Job Type, so the visible row
                  stays the two questions every job answers. */}
              {moreOptions ? null : compactDurationControl}
              {moreOptions ? null : scheduleControls}
              {specificTimeControl}
            </div>
            {/* The same disclosure Job details uses, so the two sections read
                as one form rather than two conventions. */}
            {moreOptions ? (
              <details className="group border-t border-border/50 pt-3.5">
                <summary className="flex h-6 cursor-pointer select-none list-none items-center gap-2 rounded-md text-xs font-medium leading-none text-foreground marker:content-none hover:text-foreground/80">
                  <ChevronDown className="size-3.5 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
                  More options
                  <span className="font-normal text-muted-foreground">
                    duration, visit type, time of day, urgency
                  </span>
                </summary>
                <div className="grid grid-cols-1 gap-x-5 gap-y-5 pt-4 sm:grid-cols-2 lg:grid-cols-12">
                  {compactDurationControl}
                  {moreOptions}
                  {preferenceControls}
                </div>
              </details>
            ) : null}
          </div>
        ) : showDateRange ? (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{dateRangeFields}</div>
            {durationControl}
            <div className={scheduleControlsGridClass}>{scheduleControls}</div>
            {specificTimeControl}
          </>
        ) : (
          <>
            {durationControl}
            <div className={scheduleControlsGridClass}>{scheduleControls}</div>
            {specificTimeControl}
          </>
        ))}

      {idleSlotContent ??
        (!slotsSearched ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed px-3 py-2 text-sm text-muted-foreground">
            <p>
              {!showControls
                ? 'Search available times.'
                : showDateRange
                  ? 'Search available times.'
                  : 'Search times.'}
            </p>
            {onSearchAvailability ? (
              <Button
                type="button"
                size="sm"
                disabled={searchDisabled || searching}
                onClick={onSearchAvailability}
              >
                {searching ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <Search size={14} className="mr-1.5" />
                )}
                {searchLabel}
              </Button>
            ) : null}
          </div>
        ) : (
          <AppointmentSlotPicker
            slots={slots}
            selectedSlot={selectedSlot}
            selectedSlotId={selectedSlotId}
            onSelect={onSelectSlot}
            emptyReason={emptyReason}
            dense={compactLayout}
            onChooseCrew={onChooseCrew}
            onEnterManually={onEnterManually}
            selectedTechnicianIds={selectedTechnicianIds}
            onSelectedTechniciansChange={onSelectedTechniciansChange}
          />
        ))}
    </>
  );
}
